import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DepartmentNav } from '../components/DepartmentNav';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Wheelchair, Eye, Keyboard, ChatCircle, CheckCircle } from '@phosphor-icons/react';
import { SEO } from '../components/SEO';

export const AccessibilityPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div data-testid="accessibility-page" className="min-h-screen bg-background">
      <SEO
        title="Accessibility Statement"
        description="TurfPulse's commitment to WCAG 2.1 Level AA conformance — accessibility features, compatibility, and how to report an issue."
        path="/accessibility"
      />
      {user && <DepartmentNav />}

      <main id="main-content" className="max-w-4xl mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex justify-center mb-4">
            <Wheelchair weight="duotone" size={48} className="text-primary" aria-hidden="true" />
          </div>
          <h1 className="text-4xl font-bold">Accessibility Statement</h1>
          <p className="text-muted-foreground">Last updated: December 15, 2025</p>
        </div>

        {/* Commitment */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl flex items-center gap-2">
              <CheckCircle weight="duotone" className="text-primary" size={24} aria-hidden="true" />
              Our Commitment
            </CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none text-foreground">
            <p>
              TurfPulse is committed to ensuring digital accessibility for people with disabilities. 
              We are continually improving the user experience for everyone and applying the relevant 
              accessibility standards to ensure we provide equal access to all users.
            </p>
            <p>
              We strive to conform to the <strong>Web Content Accessibility Guidelines (WCAG) 2.1 Level AA</strong> standards. 
              These guidelines explain how to make web content more accessible for people with disabilities 
              and more user-friendly for everyone.
            </p>
          </CardContent>
        </Card>

        {/* Accessibility Features */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Accessibility Features</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-6 text-foreground">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-3">
                <Eye weight="duotone" size={24} className="text-primary flex-shrink-0 mt-1" aria-hidden="true" />
                <div>
                  <h4 className="font-semibold mb-1">Visual Accessibility</h4>
                  <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
                    <li>High contrast color schemes</li>
                    <li>Dark mode support</li>
                    <li>Resizable text without loss of functionality</li>
                    <li>Alt text for meaningful images</li>
                  </ul>
                </div>
              </div>

              <div className="flex gap-3">
                <Keyboard weight="duotone" size={24} className="text-primary flex-shrink-0 mt-1" aria-hidden="true" />
                <div>
                  <h4 className="font-semibold mb-1">Keyboard Navigation</h4>
                  <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
                    <li>Full keyboard accessibility</li>
                    <li>Visible focus indicators</li>
                    <li>Skip navigation links</li>
                    <li>Logical tab order</li>
                  </ul>
                </div>
              </div>

              <div className="flex gap-3">
                <ChatCircle weight="duotone" size={24} className="text-primary flex-shrink-0 mt-1" aria-hidden="true" />
                <div>
                  <h4 className="font-semibold mb-1">Screen Reader Support</h4>
                  <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
                    <li>Semantic HTML structure</li>
                    <li>ARIA labels where needed</li>
                    <li>Descriptive link text</li>
                    <li>Form field labels</li>
                  </ul>
                </div>
              </div>

              <div className="flex gap-3">
                <Wheelchair weight="duotone" size={24} className="text-primary flex-shrink-0 mt-1" aria-hidden="true" />
                <div>
                  <h4 className="font-semibold mb-1">General Accessibility</h4>
                  <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
                    <li>Clear, consistent navigation</li>
                    <li>Descriptive page titles</li>
                    <li>Error identification and suggestions</li>
                    <li>No content that flashes more than 3 times per second</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Standards */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Conformance Status</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>
              The Web Content Accessibility Guidelines (WCAG) defines requirements for designers and 
              developers to improve accessibility for people with disabilities. It defines three levels 
              of conformance: Level A, Level AA, and Level AAA.
            </p>
            <p>
              <strong>TurfPulse targets WCAG 2.1 Level AA conformance.</strong> This means TurfPulse 
              is designed to be compatible with assistive technologies and follows accessibility best 
              practices.
            </p>
            <p>
              We are actively working to maintain and improve the accessibility of our website. 
              As we add new features, we incorporate accessibility testing into our development process.
            </p>
          </CardContent>
        </Card>

        {/* Compatibility */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Compatibility</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>TurfPulse is designed to be compatible with the following assistive technologies:</p>
            <ul>
              <li>Screen readers (NVDA, JAWS, VoiceOver, TalkBack)</li>
              <li>Screen magnification software</li>
              <li>Speech recognition software</li>
              <li>Keyboard-only navigation</li>
            </ul>
            <p>
              TurfPulse is designed to be compatible with recent versions of the following browsers:
            </p>
            <ul>
              <li>Google Chrome</li>
              <li>Mozilla Firefox</li>
              <li>Apple Safari</li>
              <li>Microsoft Edge</li>
            </ul>
          </CardContent>
        </Card>

        {/* Known Limitations */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Known Limitations</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>
              While we strive for comprehensive accessibility, some content may have limitations:
            </p>
            <ul>
              <li>
                <strong>Third-party content:</strong> Some content provided by third parties 
                (e.g., embedded maps, external widgets) may not be fully accessible.
              </li>
              <li>
                <strong>PDF documents:</strong> Some older PDF documents may not be fully accessible. 
                We are working to remediate these documents.
              </li>
              <li>
                <strong>Dynamic content:</strong> Some dynamically loaded content may require 
                additional navigation steps with screen readers.
              </li>
            </ul>
            <p>
              We are actively working to address these limitations and improve our accessibility.
            </p>
          </CardContent>
        </Card>

        {/* Feedback */}
        <Card className="border-primary/30 bg-primary/5">
          <CardHeader>
            <CardTitle className="text-xl">Feedback & Contact</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>
              We welcome your feedback on the accessibility of TurfPulse. If you encounter 
              accessibility barriers or have suggestions for improvement, please contact us:
            </p>
            <div className="bg-background border rounded-lg p-4">
              <ul className="space-y-2">
                <li>
                  <strong>Email:</strong>{' '}
                  <a 
                    href="mailto:accessibility@getturfpulse.com" 
                    className="text-primary hover:underline"
                  >
                    accessibility@getturfpulse.com
                  </a>
                </li>
                <li>
                  <strong>General Support:</strong>{' '}
                  <a 
                    href="mailto:support@getturfpulse.com" 
                    className="text-primary hover:underline"
                  >
                    support@getturfpulse.com
                  </a>
                </li>
              </ul>
            </div>
            <p>
              When contacting us about accessibility issues, please include:
            </p>
            <ul>
              <li>The web page URL where you encountered the issue</li>
              <li>A description of the problem</li>
              <li>The assistive technology you were using (if applicable)</li>
              <li>Your contact information</li>
            </ul>
            <p>
              We aim to respond to accessibility feedback within <strong>5 business days</strong> and 
              to resolve issues as quickly as possible.
            </p>
          </CardContent>
        </Card>

        {/* Legal */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Legal Compliance</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none space-y-4 text-foreground">
            <p>
              TurfPulse is committed to complying with applicable accessibility laws and regulations, including:
            </p>
            <ul>
              <li>
                <strong>Americans with Disabilities Act (ADA)</strong> — Title III requirements 
                for places of public accommodation
              </li>
              <li>
                <strong>Section 508 of the Rehabilitation Act</strong> — Accessibility standards 
                for electronic and information technology
              </li>
              <li>
                <strong>California Consumer Privacy Act (CCPA)</strong> — Accessibility of 
                privacy-related information
              </li>
              <li>
                <strong>Web Content Accessibility Guidelines (WCAG) 2.1</strong> — International 
                standard for web accessibility
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Assessment */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Assessment & Updates</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none text-foreground">
            <p>
              We assess the accessibility of TurfPulse through the following methods:
            </p>
            <ul>
              <li>Self-evaluation using accessibility testing tools</li>
              <li>Manual testing with keyboard navigation</li>
              <li>Testing with screen readers</li>
              <li>Review against WCAG 2.1 Level AA success criteria</li>
            </ul>
            <p className="mt-4">
              This statement was last updated on <strong>December 15, 2025</strong>. 
              We will update this statement as we continue to improve accessibility.
            </p>
          </CardContent>
        </Card>

        {/* Back to Home */}
        <div className="text-center pt-4">
          <Button variant="outline" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </div>
      </main>
    </div>
  );
};
